import { RenderManager } from '../../../core/RenderManager';

interface TunnelSegment {
  depth: number;      // 0.0 = at player, 1.0 = far end
  radius: number;     // Radius at this depth
  offset: Vector2;    // X/Y offset for tunnel curves
  shape: TempestShape; // Geometric shape for this segment
}

export type TempestShape = 'circle' | 'square' | 'triangle' | 'diamond' | 'hexagon' | 'octagon' | 'star' | 'plus';

interface Vector2 {
  x: number;
  y: number;
}

export class TunnelSystem {
  private renderManager: RenderManager;
  private segments: TunnelSegment[] = [];
  private segmentCount: number;
  private baseRadius: number;
  
  // Animation properties
  private animationTime: number = 0;
  private tunnelOffset: number = 0;
  private rotationSpeed: number = 0.5;
  
  // Tempest playfield properties
  private currentShape: TempestShape = 'circle';
  private shapeRotation: number = 0;
  private readonly TEMPEST_SHAPES: TempestShape[] = [
    'circle', 'square', 'triangle', 'diamond', 'hexagon', 'octagon', 'star', 'plus'
  ];
  
  // Visual properties
  private readonly SEGMENT_SPACING = 0.05; // Distance between segments in depth
  private readonly MIN_RADIUS_SCALE = 0.1;  // Minimum radius at far end
  private readonly PERSPECTIVE_SCALE = 0.8; // How much perspective affects size

  constructor(segmentCount: number, baseRadius: number, renderManager: RenderManager) {
    this.segmentCount = segmentCount;
    this.baseRadius = baseRadius;
    this.renderManager = renderManager;
    
    this.initializeSegments();
  }

  private initializeSegments(): void {
    this.segments = [];
    
    for (let i = 0; i < this.segmentCount; i++) {
      const depth = i * this.SEGMENT_SPACING;
      const perspectiveScale = Math.max(this.MIN_RADIUS_SCALE, 1 - depth * this.PERSPECTIVE_SCALE);
      
      this.segments.push({
        depth: depth,
        radius: this.baseRadius * perspectiveScale,
        offset: { x: 0, y: 0 },
        shape: this.currentShape
      });
    }
  }

  public reset(): void {
    this.animationTime = 0;
    this.tunnelOffset = 0;
    // Select random Tempest shape
    this.currentShape = this.TEMPEST_SHAPES[Math.floor(Math.random() * this.TEMPEST_SHAPES.length)];
    this.shapeRotation = Math.random() * Math.PI * 2;
    this.initializeSegments();
  }

  public update(deltaTime: number, tunnelSpeed: number): void {
    this.animationTime += deltaTime;
    this.tunnelOffset += tunnelSpeed * deltaTime;
    
    // Update each segment
    this.segments.forEach((segment, index) => {
      // Move segments forward based on tunnel speed
      segment.depth -= tunnelSpeed * deltaTime * 0.1;
      
      // Add tunnel curves and movement
      const curveInfluence = Math.sin(this.animationTime * 0.3 + index * 0.2) * 10;
      segment.offset.x = Math.sin(segment.depth * 2 + this.tunnelOffset) * curveInfluence;
      segment.offset.y = Math.cos(segment.depth * 1.5 + this.tunnelOffset * 0.8) * curveInfluence * 0.7;
      
      // Recalculate radius based on depth for perspective
      const perspectiveScale = Math.max(this.MIN_RADIUS_SCALE, 1 - segment.depth * this.PERSPECTIVE_SCALE);
      segment.radius = this.baseRadius * perspectiveScale;
    });
    
    // Remove segments that have passed the player and add new ones at the far end
    this.segments = this.segments.filter(segment => segment.depth >= -0.2);
    
    // Add new segments at the far end if needed
    while (this.segments.length < this.segmentCount) {
      const maxDepth = Math.max(...this.segments.map(s => s.depth), 0);
      const newDepth = maxDepth + this.SEGMENT_SPACING;
      const perspectiveScale = Math.max(this.MIN_RADIUS_SCALE, 1 - newDepth * this.PERSPECTIVE_SCALE);
      
      this.segments.push({
        depth: newDepth,
        radius: this.baseRadius * perspectiveScale,
        offset: { x: 0, y: 0 },
        shape: this.currentShape
      });
    }
    
    // Sort segments by depth (far to near for proper rendering)
    this.segments.sort((a, b) => b.depth - a.depth);
  }

  public render(screenWidth: number, screenHeight: number): void {
    const centerX = screenWidth / 2;
    const centerY = screenHeight / 2;
    
    // Render segments from far to near
    this.segments.forEach((segment, index) => {
      if (segment.depth < -0.1 || segment.depth > 1.2) return;
      
      const segmentCenterX = centerX + segment.offset.x;
      const segmentCenterY = centerY + segment.offset.y;
      
      // Calculate color intensity based on depth
      const intensity = Math.max(0.2, 1 - segment.depth * 0.7);
      const baseColor = Math.floor(intensity * 255);
      
      // Draw tunnel walls with perspective using Tempest shapes
      this.drawTempestShape(
        segmentCenterX, 
        segmentCenterY, 
        segment.radius, 
        segment.depth, 
        segment.shape,
        baseColor
      );
      
      // Draw radial lines for 3D effect
      if (index % 3 === 0) {
        this.drawTempestRadials(
          segmentCenterX, 
          segmentCenterY, 
          segment.radius, 
          segment.depth, 
          segment.shape,
          Math.floor(baseColor * 0.6)
        );
      }
    });
    
    // Draw tunnel entrance/exit effect
    this.drawTunnelEntrance(centerX, centerY, screenWidth, screenHeight);
  }

  private drawTempestShape(centerX: number, centerY: number, radius: number, depth: number, shape: TempestShape, intensity: number): void {
    if (radius < 2) return;
    
    const wallColor = `rgb(${intensity}, ${Math.floor(intensity * 0.8)}, ${Math.floor(intensity * 0.6)})`;
    const rotation = this.shapeRotation + this.animationTime * 0.1;
    
    const points = this.getShapePoints(centerX, centerY, radius, shape, rotation);
    
    if (points.length > 0) {
      // Draw outer wall
      this.renderManager.drawPolygon(points, wallColor, false, 2);
      
      // Draw inner shadow for depth
      const shadowPoints = this.getShapePoints(centerX, centerY, radius * 0.9, shape, rotation);
      if (shadowPoints.length > 0) {
        const shadowColor = `rgb(${Math.floor(intensity * 0.5)}, ${Math.floor(intensity * 0.4)}, ${Math.floor(intensity * 0.3)})`;
        this.renderManager.drawPolygon(shadowPoints, shadowColor, false, 1);
      }
    }
  }
  
  private getShapePoints(centerX: number, centerY: number, radius: number, shape: TempestShape, rotation: number): Vector2[] {
    const points: Vector2[] = [];
    
    switch (shape) {
      case 'circle':
        // Approximate circle with many points
        for (let i = 0; i < 16; i++) {
          const angle = (i / 16) * Math.PI * 2 + rotation;
          points.push({
            x: centerX + Math.cos(angle) * radius,
            y: centerY + Math.sin(angle) * radius
          });
        }
        break;
        
      case 'square':
        const halfSize = radius * 0.8;
        points.push(
          { x: centerX - halfSize, y: centerY - halfSize },
          { x: centerX + halfSize, y: centerY - halfSize },
          { x: centerX + halfSize, y: centerY + halfSize },
          { x: centerX - halfSize, y: centerY + halfSize }
        );
        break;
        
      case 'triangle':
        for (let i = 0; i < 3; i++) {
          const angle = (i / 3) * Math.PI * 2 + rotation - Math.PI / 2;
          points.push({
            x: centerX + Math.cos(angle) * radius,
            y: centerY + Math.sin(angle) * radius
          });
        }
        break;
        
      case 'diamond':
        points.push(
          { x: centerX, y: centerY - radius },      // Top
          { x: centerX + radius * 0.7, y: centerY }, // Right
          { x: centerX, y: centerY + radius },        // Bottom
          { x: centerX - radius * 0.7, y: centerY }  // Left
        );
        break;
        
      case 'hexagon':
        for (let i = 0; i < 6; i++) {
          const angle = (i / 6) * Math.PI * 2 + rotation;
          points.push({
            x: centerX + Math.cos(angle) * radius,
            y: centerY + Math.sin(angle) * radius
          });
        }
        break;
        
      case 'octagon':
        for (let i = 0; i < 8; i++) {
          const angle = (i / 8) * Math.PI * 2 + rotation;
          points.push({
            x: centerX + Math.cos(angle) * radius,
            y: centerY + Math.sin(angle) * radius
          });
        }
        break;
        
      case 'star':
        for (let i = 0; i < 10; i++) {
          const angle = (i / 10) * Math.PI * 2 + rotation;
          const r = (i % 2 === 0) ? radius : radius * 0.5;
          points.push({
            x: centerX + Math.cos(angle) * r,
            y: centerY + Math.sin(angle) * r
          });
        }
        break;
        
      case 'plus':
        const thickness = radius * 0.3;
        points.push(
          // Vertical bar
          { x: centerX - thickness, y: centerY - radius },
          { x: centerX + thickness, y: centerY - radius },
          { x: centerX + thickness, y: centerY - thickness },
          { x: centerX + radius, y: centerY - thickness },
          { x: centerX + radius, y: centerY + thickness },
          { x: centerX + thickness, y: centerY + thickness },
          { x: centerX + thickness, y: centerY + radius },
          { x: centerX - thickness, y: centerY + radius },
          { x: centerX - thickness, y: centerY + thickness },
          { x: centerX - radius, y: centerY + thickness },
          { x: centerX - radius, y: centerY - thickness },
          { x: centerX - thickness, y: centerY - thickness }
        );
        break;
    }
    
    return points;
  }

  private drawTempestRadials(centerX: number, centerY: number, radius: number, depth: number, shape: TempestShape, intensity: number): void {
    if (radius < 5) return;
    
    const gridColor = `rgb(${intensity}, ${intensity}, ${Math.floor(intensity * 1.2)})`;
    
    // Draw radial lines from center to shape edges
    const lineCount = shape === 'triangle' ? 3 : shape === 'square' ? 4 : shape === 'hexagon' ? 6 : shape === 'octagon' ? 8 : 12;
    
    for (let i = 0; i < lineCount; i++) {
      const angle = (i / lineCount) * Math.PI * 2 + this.shapeRotation + this.animationTime * 0.1;
      const x1 = centerX + Math.cos(angle) * radius * 0.2;
      const y1 = centerY + Math.sin(angle) * radius * 0.2;
      const x2 = centerX + Math.cos(angle) * radius * 0.9;
      const y2 = centerY + Math.sin(angle) * radius * 0.9;
      
      this.renderManager.drawLine(x1, y1, x2, y2, gridColor, 1);
    }
  }

  private drawTunnelEntrance(centerX: number, centerY: number, screenWidth: number, screenHeight: number): void {
    // Draw perspective lines from screen edges to tunnel center
    const fadeAlpha = 0.1 + Math.sin(this.animationTime * 2) * 0.05;
    
    // Top lines
    this.renderManager.drawLine(0, 0, centerX - 50, centerY - 50, `rgba(100, 150, 255, ${fadeAlpha})`, 1);
    this.renderManager.drawLine(screenWidth, 0, centerX + 50, centerY - 50, `rgba(100, 150, 255, ${fadeAlpha})`, 1);
    
    // Bottom lines  
    this.renderManager.drawLine(0, screenHeight, centerX - 50, centerY + 50, `rgba(100, 150, 255, ${fadeAlpha})`, 1);
    this.renderManager.drawLine(screenWidth, screenHeight, centerX + 50, centerY + 50, `rgba(100, 150, 255, ${fadeAlpha})`, 1);
    
    // Side lines
    this.renderManager.drawLine(0, screenHeight / 2, centerX - 50, centerY, `rgba(100, 150, 255, ${fadeAlpha})`, 1);
    this.renderManager.drawLine(screenWidth, screenHeight / 2, centerX + 50, centerY, `rgba(100, 150, 255, ${fadeAlpha})`, 1);
    
    // Central vanishing point glow
    const glowRadius = 30 + Math.sin(this.animationTime * 3) * 10;
    this.renderManager.drawCircle(centerX, centerY, glowRadius, false, `rgba(255, 255, 255, ${fadeAlpha * 0.5})`, 1);
  }

  public getTunnelBounds(screenWidth: number, screenHeight: number): { innerRadius: number, outerRadius: number } {
    const centerSegment = this.segments.find(s => s.depth <= 0.1 && s.depth >= -0.1);
    const radius = centerSegment ? centerSegment.radius : this.baseRadius;
    
    return {
      innerRadius: radius * 0.3,
      outerRadius: radius * 0.9
    };
  }
  
  public getCurrentShape(): TempestShape {
    return this.currentShape;
  }
}